import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
} from 'axios';

import { showCriticalErrorToast } from '@/helpers';

import {
  SendConfirmationEmailRequest,
  SendConfirmationEmailResponse,
  AmlCheckRequest,
  AmlCheckResponse,
  AmlHistoryRequest,
  AmlHistoryResponse,
  AssetTokenRequest,
  AssetTokenResponse,
  BaseRequest,
  BaseSuccessResponse,
  ChangeFullNameEmailRequest,
  ChangeUserPasswordRequest,
  CheckRequest,
  CheckResponse,
  ConfirmEmailRequest,
  ConfirmEmailResponse,
  CreateReflinkRequest,
  DeleteBalanceAlertResponse,
  EnterPromoCodeRequest,
  EnterPromoCodeResponse,
  ExploreConnectionRequest,
  ExploreConnectionResponse,
  ForgotPasswordEmailRequest,
  ForgotPasswordEmailResponse,
  GetAmlHistoryItemRequest,
  GetAmlHistoryItemResponse,
  GetPriceListRequest,
  GetPriceListResponse,
  GetProfileInfoRequest,
  GetProfileInfoResponse,
  GetServiceOptionsRequest,
  GetServiceOptionsResponse,
  GetTwoFACodeRequest,
  GetTwoFACodeResponse,
  GetTxReceiversRequest,
  GetTxReceiversResponse,
  GoogleAuthRequest,
  GoogleAuthResponse,
  InvoiceHistoryResponse,
  InvoiceRequest,
  InvoiceResponse,
  IssuePromoCodeRequest,
  IssuePromoCodeResponse,
  PromoHistoryRequest,
  PromoHistoryResponse,
  ReflinkResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SetBalanceAlertRequest,
  SetBalanceAlertResponse,
  SigninRequest,
  SigninResponse,
  SignoutRequest,
  SignoutResponse,
  SignupRequest,
  SignupResponse,
  StripeInvoiceResponse,
  TelegramAuthRequest,
  TelegramAuthResponse,
  UpdateCheckModeRequest,
  UpdateCheckModeResponse,
  UpdateLocaleRequest,
  UpdateLocaleResponse,
  UpdateReflinkRequest,
  UpdateTwoFAUsageRequest,
  UpdateTwoFAUsageResponse,
  UpdateUserResponse,
  VerifyRecoveryTokenRequest,
  VerifyRecoveryTokenResponse,
  WhichHashRequest,
  WhichHashResponse,
  CustomTariffRequest,
  CustomTariffResponse,
  GetAmlHistoryItemResultRequest,
  GetAmlHistoryItemResultResponse,
  ExternalPaymentRequest,
  ExternalPaymentResponse,
  ContactFormRequest,
  ContactFormResponse,
  CreateCheckBatchRequest,
  CreateCheckBatchResponse,
  CheckBatchHistoryRequest,
  CheckBatchHistoryResponse,
  ExportBatchRequest,
  ExportBatchResponse,
  BatchStateRequest,
  BatchStateResponse,
  ExportHistoryResponse,
  MonitoringSubscribeRequest,
  MonitoringSubscribeResponse,
  UserPreferencesRequest,
  AddressMonitoringListRequest,
  AddressMonitoringListResponse,
  MonitoringUnsubscribeRequest,
  MonitoringUnsubscribeResponse,
  ReceiveRemindersRequest,
  ReceiveRemindersResponse,
  UnsubscribeTokenVerifyRequest,
  UnsubscribeTokenVerifyResponse,
  UnsubscribeByTokenRequest,
  UnsubscribeByTokenResponse,
  TestEmailRequest,
  CompAuthTokenResponse,
  B2BFormTokenRequest,
  B2BFormTokenResponse,
  GetRiskProfilesResponse,
  CreateRiskProfileRequest,
  ActivateRiskProfileRequest,
  RiskProfileResponse,
  BaseRiskProfileRequest,
  RiskProfileUpdateSignalsRequest,
  RiskProfileUpdateRequest,
  OtpAuthEmailRequest,
  OtpAuthEmailResponse,
  OtpAuthOtpRequest,
  OtpAuthResponse,
  GoogleAuthOneTapRequest,
  GoogleAuthOneTapResponse,
  AmlPreviewRequest,
  AmlPreviewResponse,
  WidgetUserRequest,
  WidgetUserResponse,
  AmlTraceRequest,
  AmlTraceResponse,
  AmlTraceListRequest,
  AmlTraceListResponse,
  TracerCurrenciesResponse,
} from './models';
import { authPagesPaths } from '@/config/navigationPages';

const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: (import.meta.env.VITE_REACT_APP_AMLBOT_API_URL as string)!,
  withCredentials: true,
};

class AmlbotAPI {
  private readonly theAxios: AxiosInstance;

  private static instance: AmlbotAPI;

  private constructor(config: AxiosRequestConfig) {
    this.theAxios = axios.create(config);
    this.initResponseInterceptors();
  }

  private initResponseInterceptors = (): void => {
    this.theAxios.interceptors.response.use(
      this.handleSuccessResponse,
      this.handleErrorResponse,
    );
  };

  private handleSuccessResponse = (response: AxiosResponse) => response;

  private handleErrorResponse = (error: AxiosError) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      switch (status) {
        case HttpStatusCode.InternalServerError:
          showCriticalErrorToast();
          break;
        case HttpStatusCode.Unauthorized:
          if (
            Object.values(authPagesPaths).some((path) =>
              window.location.pathname.includes(path),
            )
          ) {
            if (window.location.pathname !== '/signin') {
              window.location.href = '/signin';
            }
          }
          break;
        default:
          break;
      }
      if (error.response?.status === HttpStatusCode.InternalServerError) {
        showCriticalErrorToast();
      }
    }

    return Promise.reject(error);
  };

  public static getInstance(
    config: AxiosRequestConfig = DEFAULT_CONFIG,
  ): AmlbotAPI {
    if (!this.instance) {
      this.instance = new AmlbotAPI(config);
    }
    return this.instance;
  }

  private post = <S, T>(
    endpoint: string,
    data: S,
    config?: AxiosRequestConfig<any>,
  ) => {
    return this.theAxios
      .post<T>(endpoint, data, config)
      .then((response: AxiosResponse<T>) => response.data);
  };

  private put = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .put<T>(endpoint, data)
      .then((response: AxiosResponse<T>) => response.data);
  };

  private patch = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .patch<T>(endpoint, data)
      .then((response: AxiosResponse<T>) => response.data);
  };

  private get = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .get<T>(endpoint, { params: data })
      .then((response: AxiosResponse<T>) => response.data);
  };

  private delete = <T>(endpoint: string) => {
    return this.theAxios
      .delete<T>(endpoint)
      .then((response: AxiosResponse<T>) => response.data);
  };

  public check = (data: CheckRequest = {}): Promise<CheckResponse> =>
    this.get<CheckRequest, CheckResponse>('/api/v2/auth/check', data);

  public current = (data: CheckRequest = {}): Promise<CheckResponse> =>
    this.get<CheckRequest, CheckResponse>('/api/v2/account/current', data);

  public signup = (data: SignupRequest): Promise<SignupResponse> =>
    this.post<SignupRequest, SignupResponse>('/api/v2/auth/signup', data);

  public signin = (data: SigninRequest): Promise<SigninResponse> =>
    this.post<SigninRequest, SigninResponse>('/api/v2/auth/signin', data);

  public googleAuth = (data: GoogleAuthRequest): Promise<GoogleAuthResponse> =>
    this.post<GoogleAuthRequest, GoogleAuthResponse>(
      '/api/v2/auth/google',
      data,
    );

  public googleAuthOneTap = (
    data: GoogleAuthOneTapRequest,
  ): Promise<GoogleAuthOneTapResponse> =>
    this.post<GoogleAuthOneTapRequest, GoogleAuthOneTapResponse>(
      '/api/v2/auth/google-one-tap',
      data,
    );

  public telegramAuth = (
    data: TelegramAuthRequest,
  ): Promise<TelegramAuthResponse> =>
    this.post<TelegramAuthRequest, TelegramAuthResponse>(
      '/api/v2/auth/telegram',
      data,
    );

  public signout = (data: SignoutRequest = {}) =>
    this.post<SignoutRequest, SignoutResponse>('/api/v2/auth/signout', data);

  public confirmEmail = (data: ConfirmEmailRequest) =>
    this.post<ConfirmEmailRequest, ConfirmEmailResponse>(
      '/api/v2/auth/confirm-email',
      data,
    );

  public passwordRecoveryEmail = (data: ForgotPasswordEmailRequest) =>
    this.post<ForgotPasswordEmailRequest, ForgotPasswordEmailResponse>(
      '/api/v2/auth/password-recovery-email',
      data,
    );

  public verifyRecoveryToken = (data: VerifyRecoveryTokenRequest) =>
    this.post<VerifyRecoveryTokenRequest, VerifyRecoveryTokenResponse>(
      '/api/v2/auth/verify-recovery-token',
      data,
    );

  public resetPassword = (data: ResetPasswordRequest) =>
    this.post<ResetPasswordRequest, ResetPasswordResponse>(
      '/api/v2/auth/reset-password',
      data,
    );

  public fetchAmlHistory = (data: AmlHistoryRequest) => {
    const searchParams: Record<string, any> = {};

    if (data.page && data.page !== 0) {
      searchParams.page = data.page.toString();
    }

    if (data.filterType) {
      searchParams.filterType = (+data.filterType).toString();
    }

    if (data.queryHash) {
      searchParams.queryHash = data.queryHash;
    }

    if (data.filter) {
      searchParams.filter = data.filter;
    }

    return this.get<AmlHistoryRequest, AmlHistoryResponse>(
      '/api/v2/aml/history/list',
      searchParams,
    );
  };

  public exportHistory = (data: AmlHistoryRequest) => {
    const searchParams: Record<string, any> = {};

    if (data.page && data.page !== 0) {
      searchParams.page = data.page.toString();
    }

    if (data.filterType) {
      searchParams.filterType = (+data.filterType).toString();
    }

    if (data.queryHash) {
      searchParams.queryHash = data.queryHash;
    }

    if (data.filter) {
      searchParams.filter = data.filter;
    }

    return this.get<AmlHistoryRequest, ExportHistoryResponse>(
      '/api/v2/aml/history/export',
      searchParams,
    );
  };

  public fetchAmlHistoryItem = (data: GetAmlHistoryItemRequest) =>
    this.post<GetAmlHistoryItemRequest, GetAmlHistoryItemResponse>(
      '/api/v2/aml/recheck',
      data,
    );

  public fetchAmlHistoryItemResult = (data: GetAmlHistoryItemResultRequest) =>
    this.get<GetAmlHistoryItemResultRequest, GetAmlHistoryItemResultResponse>(
      '/api/v2/aml/response',
      data,
    );

  public exploreConnection = (data: ExploreConnectionRequest) =>
    this.post<ExploreConnectionRequest, ExploreConnectionResponse>(
      '/api/v2/aml/explore-connection',
      data,
    );

  public amlCheck = (data: AmlCheckRequest) => {
    return this.post<AmlCheckRequest, AmlCheckResponse>(
      '/api/v2/aml/explore',
      data,
    );
  };

  public changeFullNameEmail = (data: ChangeFullNameEmailRequest) =>
    this.post<ChangeFullNameEmailRequest, UpdateUserResponse>(
      '/api/v2/account/update-profile',
      data,
    );

  public resendEmailChange = (data: BaseRequest) =>
    this.post<BaseRequest, BaseSuccessResponse>(
      '/api/v2/account/resend-email-change',
      data,
    );

  public updateLocale = (data: UpdateLocaleRequest) =>
    this.post<UpdateLocaleRequest, UpdateLocaleResponse>(
      '/api/v2/account/update-locale',
      data,
    );

  public changeUserPassword = (data: ChangeUserPasswordRequest) =>
    this.post<ChangeUserPasswordRequest, BaseSuccessResponse>(
      '/api/v2/account/update-password',
      data,
    );

  public getTwoFaCode = (data: GetTwoFACodeRequest = {}) =>
    this.get<GetTwoFACodeRequest, GetTwoFACodeResponse>(
      '/api/v2/account/get-2fa',
      data,
    );

  public updateTwoFaUsage = (data: UpdateTwoFAUsageRequest) =>
    this.post<UpdateTwoFAUsageRequest, UpdateTwoFAUsageResponse>(
      '/api/v2/account/set-2fa',
      data,
    );

  public getWhichHash = (data: WhichHashRequest) =>
    this.post<WhichHashRequest, WhichHashResponse>(
      '/api/v2/aml/which-hash',
      data,
    );

  public updateCheckMode = (data: UpdateCheckModeRequest) =>
    this.post<UpdateCheckModeRequest, UpdateCheckModeResponse>(
      '/api/v2/account/aml-flow',
      data,
    );

  public getProfileInfo = (data: GetProfileInfoRequest = {}) =>
    this.get<GetProfileInfoRequest, GetProfileInfoResponse>(
      '/api/v2/account/info',
      data,
    );

  public setAlertPeriod = (data: SetBalanceAlertRequest) =>
    this.post<SetBalanceAlertRequest, SetBalanceAlertResponse>(
      '/api/v2/account/balancealerts',
      data,
    );

  public confirmTerms = (data: BaseRequest) =>
    this.post<BaseRequest, CheckResponse>(
      '/api/v2/account/confirm-terms',
      data,
    );

  public changeReceiveReminders = (data: ReceiveRemindersRequest) =>
    this.post<ReceiveRemindersRequest, ReceiveRemindersResponse>(
      '/api/v2/reminders',
      data,
    );

  public updatePreferences = (data: UserPreferencesRequest) =>
    this.post<UserPreferencesRequest, BaseSuccessResponse>(
      '/api/v2/account/preferences',
      data,
    );

  public deleteAlertPeriod = () =>
    this.delete<DeleteBalanceAlertResponse>('/api/v2/account/balancealerts');

  public fetchPromoHistory = (data: PromoHistoryRequest = {}) => {
    const requestParams: Record<string, string> = {};

    if (data.page && data.page !== 0) {
      requestParams.page = data.page.toString();
    }

    return this.get<PromoHistoryRequest, PromoHistoryResponse>(
      '/api/v2/account/promo-history',
      requestParams,
    );
  };

  public issuePromoCode = (data: IssuePromoCodeRequest) =>
    this.post<IssuePromoCodeRequest, IssuePromoCodeResponse>(
      '/api/v2/account/promo',
      data,
    );

  public fetchRefLinks = (data: BaseRequest) =>
    this.get<BaseRequest, ReflinkResponse>('/api/v2/account/reflinks', data);

  public createReflink = (data: CreateReflinkRequest) =>
    this.post<CreateReflinkRequest, ReflinkResponse>(
      '/api/v2/account/reflinks',
      data,
    );

  public updateReflink = (data: UpdateReflinkRequest) =>
    this.put<UpdateReflinkRequest, ReflinkResponse>(
      '/api/v2/account/reflinks',
      data,
    );

  public fetchServiceOptions = (data: GetServiceOptionsRequest) =>
    this.get<GetServiceOptionsRequest, GetServiceOptionsResponse>(
      '/api/v2/service-options',
      data,
    );

  public fetchTokens = (data: AssetTokenRequest) =>
    this.get<AssetTokenRequest, AssetTokenResponse>('/api/v2/aml/tokens', data);

  public fetchPriceList = (data: GetPriceListRequest = {}) =>
    this.get<GetPriceListRequest, GetPriceListResponse>(
      '/api/v2/balance/prices',
      data,
    );

  public enterPromoCode = (data: EnterPromoCodeRequest) =>
    this.post<EnterPromoCodeRequest, EnterPromoCodeResponse>(
      '@/api/v2/balance/invoice',
      data,
    );

  public createStripeInvoice = (data: InvoiceRequest) =>
    this.post<InvoiceRequest, StripeInvoiceResponse>(
      '/api/v2/balance/stripe-invoice',
      data,
    );

  public createCryptoInvoice = (data: InvoiceRequest) =>
    this.post<InvoiceRequest, InvoiceResponse>('/api/v2/balance/invoice', data);

  public fetchInvoiceHistory = (data: BaseRequest) =>
    this.get<BaseRequest, InvoiceHistoryResponse[]>(
      '/api/v2/balance/invoices',
      data,
    );

  public sendConfirmationEmail = (data: SendConfirmationEmailRequest = {}) =>
    this.post<SendConfirmationEmailRequest, SendConfirmationEmailResponse>(
      '/api/v2/account/send-confirmation-email',
      data,
    );

  public fetchReceivers = (data: GetTxReceiversRequest) =>
    this.get<GetTxReceiversRequest, GetTxReceiversResponse>(
      '/api/v2/aml/tx-receivers',
      data,
    );

  public requestCustomTariff = (data: CustomTariffRequest) =>
    this.post<CustomTariffRequest, CustomTariffResponse>(
      '/api/v2/balance/request-custom-plan',
      data,
    );

  public getExternalPayment = (data: ExternalPaymentRequest) =>
    this.get<ExternalPaymentRequest, ExternalPaymentResponse>(
      '/api/v2/pay',
      data,
    );

  public sendContactForm = (data: ContactFormRequest) =>
    this.post<ContactFormRequest, ContactFormResponse>(
      '/api/v2/account/contact',
      data,
    );

  public createBatch = (data: CreateCheckBatchRequest) =>
    this.post<CreateCheckBatchRequest, CreateCheckBatchResponse>(
      '/api/v2/batches',
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

  public fetchBatchHistory = (data: CheckBatchHistoryRequest) =>
    this.get<CheckBatchHistoryRequest, CheckBatchHistoryResponse>(
      '/api/v2/batches',
      data,
    );

  public exportBatch = (data: ExportBatchRequest) =>
    this.get<ExportBatchRequest, ExportBatchResponse>(
      '/api/v2/batch/export',
      data,
    );

  public refreshBatchItem = (data: BatchStateRequest) =>
    this.post<BatchStateRequest, BatchStateResponse>(
      '/api/v2/batch/state',
      data,
    );

  public monitoringSubscribe = (data: MonitoringSubscribeRequest) =>
    this.post<MonitoringSubscribeRequest, MonitoringSubscribeResponse>(
      '/api/v2/monitoring/subscribe',
      data,
    );

  public fetchMonitoringList = (data: AddressMonitoringListRequest) =>
    this.get<AddressMonitoringListRequest, AddressMonitoringListResponse>(
      '/api/v2/monitoring/list',
      data,
    );

  public monitoringUnsubscribe = (data: MonitoringUnsubscribeRequest) =>
    this.post<MonitoringUnsubscribeRequest, MonitoringUnsubscribeResponse>(
      '/api/v2/monitoring/unsubscribe',
      data,
    );

  public verifyUnsubscribeToken = (data: UnsubscribeTokenVerifyRequest) =>
    this.post<UnsubscribeTokenVerifyRequest, UnsubscribeTokenVerifyResponse>(
      '/api/v2/reminders/token',
      data,
    );

  public unsubscribeByToken = (data: UnsubscribeByTokenRequest) =>
    this.post<UnsubscribeByTokenRequest, UnsubscribeByTokenResponse>(
      '/api/v2/reminders/unsubscribe',
      data,
    );

  public getComplianceAuthToken = () =>
    this.get<null, CompAuthTokenResponse>(
      '/api/v2/account/compliance-token',
      null,
    );

  public testEmail = (data: TestEmailRequest) =>
    this.post<TestEmailRequest, BaseSuccessResponse>('/api/test/email', data);

  public usagePicked = (data: BaseRequest) =>
    this.post<BaseRequest, BaseSuccessResponse>(
      '/api/v2/account/pick-usage',
      data,
    );

  public fetchB2BTokenData = (data: B2BFormTokenRequest) =>
    this.post<B2BFormTokenRequest, B2BFormTokenResponse>(
      '/api/v2/bussiness/token',
      data,
    );

  public completeB2BForm = (data: B2BFormTokenRequest) =>
    this.post<B2BFormTokenRequest, B2BFormTokenResponse>(
      '/api/v2/bussiness/completed',
      data,
    );

  public listRiskProfiles = (data: BaseRequest = {}) =>
    this.get<BaseRequest, GetRiskProfilesResponse>(
      '/api/v2/account/risk-profiles',
      data,
    );

  public createRiskProfile = (data: CreateRiskProfileRequest) =>
    this.post<CreateRiskProfileRequest, BaseSuccessResponse>(
      '/api/v2/account/risk-profiles',
      data,
    );

  public activateRiskProfile = (data: ActivateRiskProfileRequest) =>
    this.post<ActivateRiskProfileRequest, RiskProfileResponse>(
      '/api/v2/account/risk-profiles/activate',
      data,
    );

  public fetchRiskProfile = (data: BaseRiskProfileRequest) =>
    this.get<BaseRiskProfileRequest, RiskProfileResponse>(
      '/api/v2/account/risk-profile',
      data,
    );

  public updateRiskProfile = (data: RiskProfileUpdateRequest) =>
    this.post<RiskProfileUpdateRequest, RiskProfileResponse>(
      '/api/v2/account/risk-profile/update',
      data,
    );

  public setProfileSignals = (data: RiskProfileUpdateSignalsRequest) =>
    this.post<RiskProfileUpdateSignalsRequest, RiskProfileResponse>(
      '/api/v2/account/risk-profiles/signals',
      data,
    );

  public resetRiskProfile = (data: BaseRiskProfileRequest) =>
    this.post<BaseRiskProfileRequest, RiskProfileResponse>(
      '/api/v2/account/risk-profiles/reset',
      data,
    );

  public deleteRiskProfile = (data: BaseRiskProfileRequest) =>
    this.post<BaseRiskProfileRequest, RiskProfileResponse>(
      '/api/v2/account/risk-profiles/delete',
      data,
    );

  public onboardOtpEmail = (data: OtpAuthEmailRequest) =>
    this.post<OtpAuthEmailRequest, OtpAuthEmailResponse>(
      '/verif/onboard',
      data,
    );

  public verifyOtp = (data: OtpAuthOtpRequest) =>
    this.post<OtpAuthOtpRequest, OtpAuthResponse>('/verif/verify-otp', data);

  public fetchAmlPreview = (data: AmlPreviewRequest) =>
    this.get<AmlPreviewRequest, AmlPreviewResponse>(
      '/api/v2/aml/preview',
      data,
    );

  public discardAmlDetailsFor = (data: BaseRequest) =>
    this.post<BaseRequest, BaseSuccessResponse>(
      '/api/v2/account/discard-details-for',
      data,
    );

  public startTrace = (data: AmlTraceRequest): Promise<AmlTraceResponse> =>
    this.post<AmlTraceRequest, AmlTraceResponse>(
      'api/event-tracer/trace',
      data,
    );

  public tracingHistory = (
    data: AmlTraceListRequest,
  ): Promise<AmlTraceListResponse> =>
    this.get<AmlTraceListRequest, AmlTraceListResponse>(
      'api/event-tracer/list',
      data,
    );

  public tracerCurrencies = (
    data: BaseRequest,
  ): Promise<TracerCurrenciesResponse> =>
    this.get<BaseRequest, TracerCurrenciesResponse>(
      'api/event-tracer/currencies',
      data,
    );

  public registerWidgetUser = (data: WidgetUserRequest) =>
    this.post<WidgetUserRequest, WidgetUserResponse>('/api/widget/user', data);
}

const amlbotAPI = AmlbotAPI.getInstance();

export default amlbotAPI;
